import { useEffect } from "react";
import { Heart, Play, ShoppingCart, X } from "lucide-react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { useModalScrollbar } from "@/hooks/useModalScrollbar";
import { buildVimeoEmbedUrl, formatDuration } from "@/lib/helpers";
import type { CourseWithDetails } from "@/lib/schema";
import { cn } from "@/lib/utils";

interface LibraryPreviewModalProps {
  course: CourseWithDetails;
  instructorBio: string;
  instructorImage: string;
  isLoggedIn: boolean;
  isOwned: boolean;
  isSaved: boolean;
  inCart: boolean;
  addingFree: boolean;
  loginHref: string;
  onAddFree: (courseId: string) => void;
  onAddToCart: (course: CourseWithDetails) => void;
  onClose: () => void;
  onToggleSaved: () => void;
}

export function LibraryPreviewModal({
  course,
  instructorBio,
  instructorImage,
  isLoggedIn,
  isOwned,
  isSaved,
  inCart,
  addingFree,
  loginHref,
  onAddFree,
  onAddToCart,
  onClose,
  onToggleSaved,
}: LibraryPreviewModalProps) {
  const { scrollRef: contentScrollRef, scrollbar } = useModalScrollbar([course.id]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    // Lock body scroll while modal is open
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [onClose]);

  const duration = course.total_duration ? formatDuration(course.total_duration) : null;
  const primaryLabel = getPrimaryLabel(course, isLoggedIn, isOwned, inCart, addingFree);

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-50 bg-[rgba(29,13,24,0.58)]"
        aria-label="Close preview"
        onClick={onClose}
      />
      <div
        className="fixed left-1/2 top-1/2 z-[60] grid h-[min(48rem,calc(100vh-2rem))] w-[min(70rem,calc(100vw-2rem))] -translate-x-1/2 -translate-y-1/2 grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] overflow-hidden rounded-[2rem] bg-white shadow-[0_40px_120px_rgba(31,11,23,0.3)] max-[991px]:max-h-[calc(100vh-1.5rem)] max-[991px]:w-[min(42rem,calc(100vw-1.5rem))] max-[991px]:grid-cols-1 max-[991px]:grid-rows-[auto_minmax(0,1fr)] max-[479px]:w-[calc(100vw-1rem)] max-[479px]:max-h-[calc(100vh-1rem)]"
        role="dialog"
        aria-modal="true"
        aria-label={`${course.name} preview`}
      >
        <button
          type="button"
          className="absolute right-4 top-4 z-[1] inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-[#351c2a]"
          aria-label="Close preview"
          onClick={onClose}
        >
          <X className="h-5 w-5" />
        </button>

        <div className="min-h-0 bg-[#ece5ea] max-[991px]:h-[clamp(12rem,32vh,18rem)]">
          {course.preview_video_id?.video_url ? (
            <iframe
              src={buildVimeoEmbedUrl(course.preview_video_id.video_url, {
                autoplay: true,
                muted: true,
              })}
              className="h-full w-full border-0"
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
              title={`Preview: ${course.name}`}
            />
          ) : course.thumbnail_links_course ? (
            <img
              src={course.thumbnail_links_course}
              alt={course.name}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#b3839f] to-[#6d2d58]">
              <Play className="h-10 w-10" />
            </div>
          )}
        </div>

        <div className="relative min-h-0">
          <div
            ref={contentScrollRef}
            className="modal-scrollbar flex h-full min-h-0 flex-col gap-[1.35rem] overflow-y-auto p-8 pr-[3.25rem] max-[991px]:pr-[3rem] max-[479px]:p-[1.15rem] max-[479px]:pr-[2.75rem]"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="inline-flex items-center rounded-full bg-[#fff2fb] px-[0.8rem] py-[0.45rem] text-[0.82rem] font-[800] tracking-[-0.02em] text-[#9b4479]">
                {course.is_free ? "FREE" : course.most_popular ? "Most Popular" : "Tutorial"}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  className={cn(
                    "inline-flex h-6 w-6 items-center justify-center border-none bg-transparent text-[#3d3439] transition-colors duration-[180ms] ease-in-out hover:text-[#762a5c]",
                    isSaved && "text-[#762a5c]"
                  )}
                  aria-label={isSaved ? "Remove from saved" : "Save tutorial"}
                  onClick={onToggleSaved}
                >
                  <Heart className={cn("h-4 w-4", isSaved && "fill-current")} />
                </button>
              </div>
            </div>

            <div>
              <h2 className="m-0 font-display text-[2rem] leading-none text-[#341928] max-[767px]:text-[1.7rem]">{course.name}</h2>
              {(course.long_description ?? course.short_description) ? (
                <p
                  className="m-0 text-[0.95rem] leading-[1.65] text-[#5a5658] [&_a]:text-[#8b2252] [&_a]:underline"
                  dangerouslySetInnerHTML={{ __html: course.long_description ?? course.short_description! }}
                />
              ) : (
                <p className="m-0 text-[0.95rem] leading-[1.65] text-[#5a5658]">
                  Practice with a polished, studio-quality tutorial designed for celebration moments.
                </p>
              )}
            </div>

            <div className="flex flex-wrap gap-[0.55rem]">
              {duration ? <span className="inline-flex items-center rounded-full bg-[#f4eef2] px-[0.72rem] py-[0.38rem] text-[0.78rem] font-bold text-[#5b5258]">{duration} total</span> : null}
              {course.total_videos != null ? (
                <span className="inline-flex items-center rounded-full bg-[#f4eef2] px-[0.72rem] py-[0.38rem] text-[0.78rem] font-bold text-[#5b5258]">
                  {course.total_videos} video{course.total_videos === 1 ? "" : "s"}
                </span>
              ) : null}
              {(course.category ?? []).slice(0, 3).map((tag) => (
                <span key={tag.name} className="inline-flex items-center rounded-full bg-[#f4eef2] px-[0.72rem] py-[0.38rem] text-[0.78rem] font-bold text-[#5b5258]">{tag.name}</span>
              ))}
            </div>

            <div className="grid grid-cols-[4rem_minmax(0,1fr)] items-start gap-4 rounded-[1.25rem] bg-[#faf6f8] p-4">
              <img
                src={instructorImage}
                alt={course.Instructor?.name ?? "Instructor"}
                className="h-16 w-16 rounded-[1rem] object-cover"
              />
              <div>
                <div className="text-[0.65rem] font-bold uppercase tracking-[0.06em] text-[#b0a4aa]">Instructor</div>
                <div className="text-[0.96rem] font-normal text-[#2f2830]">
                  {course.Instructor?.name ?? "Khusbu Gemmati"}
                </div>
                <p className="m-0 text-[0.95rem] leading-[1.65] text-[#5a5658]">{instructorBio}</p>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              {!isLoggedIn && !isOwned ? (
                <Button asChild className="inline-flex h-auto min-h-[51px] w-full items-center justify-center gap-2 rounded-[1rem] border border-[#762a5c] bg-[#762a5c] px-8 py-4 font-[Inter,sans-serif] text-[14px] font-medium leading-none text-white shadow-none hover:bg-[#5f2349]">
                  <Link to={loginHref}>{primaryLabel}</Link>
                </Button>
              ) : isOwned ? (
                <Button type="button" className="inline-flex h-auto min-h-[51px] w-full items-center justify-center gap-2 rounded-[1rem] border border-[#cef5ca] bg-[#cef5ca] px-8 py-4 font-[Inter,sans-serif] text-[14px] font-medium leading-none text-[#114e0b] shadow-none" disabled>
                  {primaryLabel}
                </Button>
              ) : course.is_free ? (
                <Button
                  type="button"
                  variant="outline"
                  className="inline-flex h-auto min-h-[51px] w-full items-center justify-center gap-2 rounded-[1rem] border border-[#762a5c] bg-white px-8 py-4 font-[Inter,sans-serif] text-[14px] font-medium leading-none text-[#762a5c] shadow-none hover:bg-[#faf1f6] hover:text-[#762a5c]"
                  disabled={addingFree}
                  onClick={() => onAddFree(course.id)}
                >
                  {primaryLabel}
                </Button>
              ) : (
                <Button
                  type="button"
                  className={cn(
                    "inline-flex h-auto min-h-[51px] w-full items-center justify-center gap-2 rounded-[1rem] border border-[#762a5c] bg-[#762a5c] px-8 py-4 font-[Inter,sans-serif] text-[14px] font-medium leading-none text-white shadow-none hover:bg-[#5f2349]",
                    inCart && "bg-white text-[#762a5c]"
                  )}
                  onClick={() => onAddToCart(course)}
                >
                  <ShoppingCart className="h-4 w-4" />
                  {primaryLabel}
                </Button>
              )}

              <Button
                asChild
                variant="outline"
                className="rounded-full border-[#d7c3d0] text-[#5f3350] hover:bg-[#faf1f6]"
              >
                <Link to={`/app/tutorial/${course.id}`}>View Tutorial Details</Link>
              </Button>
            </div>
          </div>

          {scrollbar.visible ? (
            <div className="pointer-events-none absolute inset-y-4 right-[0.95rem] w-[0.55rem]">
              <div
                className="absolute left-1/2 w-[0.38rem] -translate-x-1/2 rounded-full bg-[#9c989a]"
                style={{
                  height: `${scrollbar.thumbHeight}px`,
                  top: `${scrollbar.thumbOffset}px`,
                }}
              />
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}

function getPrimaryLabel(
  course: CourseWithDetails,
  isLoggedIn: boolean,
  isOwned: boolean,
  inCart: boolean,
  addingFree: boolean,
) {
  if (isOwned) return "Purchased";
  if (!isLoggedIn) return "Login to Purchase";
  if (course.is_free) return addingFree ? "Adding..." : "Add to Dashboard";
  if (inCart) return "Added to Cart";
  return "Add to Cart";
}
